import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "../lib/adminApi";
import { Field, TextInput, TextArea, PageHeader, SaveBar } from "./formKit";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// If the full <meta ...> tag is pasted, keep only the content attribute value.
function extractGsvCode(raw) {
  const t = (raw || "").trim();
  const m = t.match(/content\s*=\s*["']([^"']*)["']/i);
  return (m ? m[1] : t).trim();
}

export default function AdminContactInfoPage() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifyEmailError, setNotifyEmailError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await adminApi.get("/admin/contact-info");
        setForm({
          phone: "",
          email: "",
          address: "",
          hours: "",
          contact_page_message: "",
          social_links: {},
          inquiry_notification_email: "",
          smtp_from_name: "",
          google_site_verification: "",
          ...data,
        });
      } catch {
        toast.error("Couldn't load contact info.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k) => (v) =>
    setForm((f) => ({ ...f, social_links: { ...(f.social_links || {}), [k]: v } }));

  const setNotifyEmail = (v) => {
    setForm((f) => ({ ...f, inquiry_notification_email: v }));
    if (!v) {
      setNotifyEmailError("");
    } else if (!EMAIL_RE.test(v.trim())) {
      setNotifyEmailError("Must be a valid email address.");
    } else {
      setNotifyEmailError("");
    }
  };

  const save = async () => {
    const notifyRaw = (form.inquiry_notification_email || "").trim();
    if (notifyRaw && !EMAIL_RE.test(notifyRaw)) {
      setNotifyEmailError("Must be a valid email address.");
      toast.error("Inquiry Notification Email is not a valid email address.");
      return;
    }
    setSaving(true);
    try {
      await adminApi.put("/admin/contact-info", {
        ...form,
        inquiry_notification_email: notifyRaw,
        smtp_from_name: (form.smtp_from_name || "").trim(),
        google_site_verification: extractGsvCode(form.google_site_verification),
      });
      toast.success("Contact info saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || "invalid").join("; ")
        : typeof detail === "string"
        ? detail
        : "Couldn't save.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div data-testid="admin-contact-info-page">
      <PageHeader
        title="Contact info"
        subtitle="Used in the Contact page, footer, and anywhere phone/email/address appear."
      />
      <div className="rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone">
            <TextInput
              value={form.phone}
              onChange={set("phone")}
              data-testid="contact-info-phone"
            />
          </Field>
          <Field label="Email">
            <TextInput
              value={form.email}
              onChange={set("email")}
              data-testid="contact-info-email"
            />
          </Field>
        </div>
        <Field label="Address">
          <TextInput value={form.address} onChange={set("address")} />
        </Field>
        <Field label="Hours">
          <TextInput value={form.hours} onChange={set("hours")} />
        </Field>
        <Field label="Contact page message">
          <TextArea
            rows={4}
            value={form.contact_page_message}
            onChange={set("contact_page_message")}
          />
        </Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Instagram URL">
            <TextInput
              value={form.social_links?.instagram || ""}
              onChange={setSocial("instagram")}
            />
          </Field>
          <Field label="Facebook URL">
            <TextInput
              value={form.social_links?.facebook || ""}
              onChange={setSocial("facebook")}
            />
          </Field>
          <Field label="LinkedIn URL">
            <TextInput
              value={form.social_links?.linkedin || ""}
              onChange={setSocial("linkedin")}
            />
          </Field>
        </div>
      </div>

      {/* Inquiry notifications — routes only; SMTP credentials remain in .env */}
      <div
        className="mt-8 rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-5"
        data-testid="admin-contact-info-notifications"
      >
        <div>
          <h2 className="font-display text-xl font-extrabold tracking-tight text-slate-900">
            Inquiry notifications
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Controls where new parent inquiries are delivered. SMTP credentials
            (host, user, password) are managed in the server environment and are
            never editable or visible here.
          </p>
        </div>
        <Field
          label="Inquiry Notification Email"
          hint="New parent inquiry notifications will be sent to this email address. Leave blank to fall back to the server default."
          error={notifyEmailError}
        >
          <TextInput
            type="email"
            value={form.inquiry_notification_email || ""}
            onChange={setNotifyEmail}
            placeholder="admissions@nexora.app"
            data-testid="contact-info-inquiry-notification-email"
          />
        </Field>
        <Field
          label="Notification From Name"
          hint="Display name shown on outgoing notification emails. Defaults to Nexora Learning & Development Centre."
        >
          <TextInput
            value={form.smtp_from_name || ""}
            onChange={set("smtp_from_name")}
            placeholder="Nexora Learning & Development Centre"
            data-testid="contact-info-smtp-from-name"
          />
        </Field>
      </div>

      {/* SEO settings */}
      <div
        className="mt-8 rounded-[2rem] bg-white border border-slate-200 p-6 sm:p-8 grid gap-5"
        data-testid="admin-contact-info-seo"
      >
        <div>
          <h2 className="font-display text-xl font-extrabold tracking-tight text-slate-900">
            SEO settings
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Site ownership verification for Google Search Console. When set, the
            verification meta tag is added to every public page.
          </p>
        </div>
        <Field
          label="Google Site Verification Code"
          hint="Paste only the verification code from Google Search Console, not the full meta tag. If you paste the full tag, we'll extract the code for you. Leave blank to remove the tag."
        >
          <TextInput
            value={form.google_site_verification || ""}
            onChange={set("google_site_verification")}
            placeholder="abc123xyz"
            data-testid="contact-info-google-site-verification"
          />
        </Field>
      </div>

      <SaveBar onSave={save} saving={saving} />
    </div>
  );
}
